import React, { useState, useRef, useEffect } from "react";
import history from "@src/history";
import { Button } from "semantic-ui-react";
import TwoFactorIllustration from "@components/illustrations/two-factor-illustration";
import * as Styles from "@components/OTPVerification/styles";
import { AuthorizationVerifyMfa } from "@src/services/requests/Authorization";
import { encryptWithPublicKey } from "@src/utils/encryptCertification";
import { Check } from "lucide-react";
import { login } from "@services/auth";
import { ArrowLeft } from "lucide-react";

export default function OTPVerification(props) {
  const {
    channel,
    phoneNumber,
    sessionId,
    cooldownSeconds,
    responseData,
    passwdExpired,
    passwdDaysToExpire,
    passwdMessage,
  } = props.location?.state || {};

  const onVerify = props.onVerify || null;

  const [codigoOtp, setCodigoOtp] = useState(["", "", "", "", "", ""]);
  const [tempoRestante, setTempoRestante] = useState(cooldownSeconds || 90);
  const [podeReenviar, setPodeReenviar] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const inputRefs = useRef([]);
  const [, setMensagem] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!phoneNumber && !passwdExpired) {
      history.push("/login");
    }
  }, [phoneNumber, passwdExpired]);

  useEffect(() => {
    if (passwdExpired) {
      setMensagem(
        "Sua senha expirou. É necessário redefini-la para continuar acessando."
      );
      return; 
    }

    if (passwdDaysToExpire && passwdDaysToExpire > 0) {
      setError("");
      setMensagem(
        `Sua senha expirará em ${passwdDaysToExpire} dias. ${passwdMessage || ""}`
      );
    }
  }, [passwdExpired, passwdDaysToExpire, passwdMessage]);

  useEffect(() => {
    if (responseData && responseData?.access_token) {
      login(responseData, false);
      if (typeof onVerify === "function") onVerify(true);
      history.push("/dashboard");
    }
  }, [onVerify, responseData]);

  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 768);
      setIsTablet(width > 768 && width <= 1422);
    };
    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  useEffect(() => {
    if (!podeReenviar) {
      const interval = setInterval(() => {
        setTempoRestante((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            setPodeReenviar(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [podeReenviar]);

  const handleOtpChange = (e, index) => {
    const { value } = e.target;
    if (value.length > 1 || (value && !/^\d$/.test(value))) return;
    const newCodigo = [...codigoOtp];
    newCodigo[index] = value;
    setCodigoOtp(newCodigo);
    if (value && index < codigoOtp.length - 1) {
      const nextEl = inputRefs.current[index + 1];
      if (nextEl) nextEl.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !codigoOtp[index] && index > 0) {
      const prevEl = inputRefs.current[index - 1];
      if (prevEl) prevEl.focus();
    }
  };

  const handleConfirmar = async () => {
    setCarregando(true);
    const codigoCompleto = codigoOtp.join("");

    if (!codigoCompleto) {
      setCarregando(false);
      return;
    }

    const payload = { sessionId, code: codigoCompleto };
    const encryptedData = await encryptWithPublicKey(
      JSON.stringify(payload)
    ).catch(() => null);

    const response = await AuthorizationVerifyMfa({ data: encryptedData }).catch(
      () => {
        setCarregando(false);
        setError("Erro ao verificar o código. Tente novamente.");
        return null;
      }
    );

    if (response && response.status === 200) {
      login(response.data, false);
      if (typeof onVerify === "function") onVerify(true);
      history.push("/dashboard");
    } else {
      setCarregando(false);
      setError(response?.data?.mensagem || "Erro ao verificar o código. Tente novamente.");
    }

    setCarregando(false);
  };

  const handleReenviar = () => {
    if (podeReenviar) {
      setCodigoOtp(["", "", "", "", "", ""]);
      setTempoRestante(90);
      setPodeReenviar(false);
    }
  };

  const voltarParaLogin = () => history.push("/login");

  const formatarTempo = (s) => {
    const min = Math.floor(s / 60);
    const sec = s % 60;
    return `${String(min).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  };

  const handleIrParaRedefinir = () => {
    history.push("/recuperar-senha", {
      passwdMessage:
        passwdMessage || "Sua senha expirou. É necessário redefini-la.",
    });
  };

  const ExpiredBox = () => {

    return (
      <div style={Styles.boxWrap}>
        <div style={Styles.card}>
          <div style={Styles.headerRow}>
            <div style={Styles.checkIcon}>
              <Check size={20} color="green" />
            </div>
            <div style={Styles.title}>Senha expirada</div>
          </div>

          <div style={Styles.text}>
            Sua senha expirou. Para continuar, você precisa redefinir sua senha.
            Clique no botão abaixo para ir para a página de redefinição.{" "}
            {passwdMessage ? <em>{passwdMessage}</em> : null}
          </div>

          <div style={Styles.actions}>
            <button style={Styles.primary} onClick={handleIrParaRedefinir}>
              Redefinir senha
            </button>
            <button style={Styles.ghost} onClick={voltarParaLogin}>
              Voltar ao login
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderFormContent = () => (
    <div style={Styles.formBox}>
      <div style={Styles.containerForm}>
        <div style={Styles.filhaForm}>
          {passwdDaysToExpire > 0 && (
            <div
              style={{
                padding: 12,
                borderRadius: 6,
                background: "#fff4e5",
                marginBottom: 12,
              }}
            >
              Sua senha expira em <strong>{passwdDaysToExpire} dias</strong>.{" "}
              {passwdMessage}
            </div>
          )}

          <h2 style={Styles.titleStyle}>Verifique seu celular!</h2>
          <p style={Styles.subtitleStyle}>
            Um código de verificação foi enviado via{" "}
            {channel ? String(channel).toUpperCase() : "SMS"} para (
            <strong>+{phoneNumber}</strong>)
          </p>

          {error && <p style={Styles.errorStyle}>{error}</p>}

          <div style={Styles.otpContainer}>
            {codigoOtp.map((val, i) => (
              <input
                key={i}
                ref={(el) => (inputRefs.current[i] = el)}
                type="text"
                maxLength={1}
                value={val}
                placeholder="-"
                autoFocus={i === 0}
                onChange={(e) => handleOtpChange(e, i)}
                onKeyDown={(e) => handleKeyDown(e, i)}
                disabled={carregando}
                style={Styles.otpInputStyle}
              />
            ))}
          </div>

          <Button
            primary
            fluid
            size="big"
            loading={carregando}
            onClick={handleConfirmar}
            disabled={carregando}
            style={Styles.verifyButtonStyle}
          >
            Verificar
          </Button>

          <div style={Styles.resendContainer}>
            <button onClick={voltarParaLogin} style={Styles.botaoVoltar}>
              <ArrowLeft size={18} />
              Voltar
            </button>
            {podeReenviar ? (
              <button
                onClick={handleReenviar}
                disabled={carregando}
                style={Styles.resendButtonStyle}
              >
                Reenviar
              </button>
            ) : (
              <span style={Styles.timerStyle}>
                Reenviar em <strong>{formatarTempo(tempoRestante)}</strong>
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderSVG = () => (
    <div
      style={
        isMobile
          ? Styles.imageBoxMobile
          : isTablet
            ? Styles.imageBoxTablet
            : Styles.imageBox
      }
    >
      <TwoFactorIllustration />
    </div>
  );

  if (passwdExpired) {
    return <ExpiredBox />;
  }

  if (isMobile) {
    return (
      <div style={Styles.containerMobile}>
        <div style={Styles.leftPanelMobile}>{renderFormContent()}</div>
        {renderSVG()}
      </div>
    );
  }

  if (isTablet) {
    return (
      <div style={Styles.containerTablet}>
        <div style={Styles.leftPanelTablet}>{renderFormContent()}</div>
        {renderSVG()}
      </div>
    );
  }

  return (
    <div style={Styles.container}>
      <div style={Styles.leftPanel}>{renderFormContent()}</div>
      <div style={Styles.rightPanel}></div>
      {renderSVG()}
    </div>
  );
}
